import React, { useState } from 'react';
import { KFDropdown, KFSearchbar } from '@kf/ic2fx';
import {
  Box,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  Typography,
  Tooltip,
} from '@mui/material';
import { colors } from 'styles/colors';
import { IRequisition } from 'modules/hiring/models/myOpenRequisition.model';
import { getSearchResults } from 'utilities/getSearchResults';
import { withTranslation, WithTranslation } from 'react-i18next';

type TKFSearchbarProps = Parameters<typeof KFSearchbar>[0];

export interface IDropdownWithSearchProps extends TKFSearchbarProps {
  id: string;
  defaultSelected: IRequisition | null;
  options: IRequisition[];
  handleSelection: (requisitionID: IRequisition | null) => void;
}
type TDropdownWithSearchbarProps = IDropdownWithSearchProps & WithTranslation;

const DropdownWithSearch = ({
  t: translate,
  tReady,
  id,
  options,
  handleSelection,
  defaultSelected,
  ...props
}: TDropdownWithSearchbarProps) => {
  const allReqValue = 'allReq';
  const theme = useTheme();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [searchString, setSearchString] = useState<string>('');
  const [visibleOptions, setVisibleOptions] = useState<IRequisition[]>(options);

  const [selected, setSelected] = useState<string | null>(
    defaultSelected?.requisitionID ?? allReqValue
  );

  const handleSelectionChange = (event: SelectChangeEvent<unknown>) => {
    setSelected(event.target.value as string);
    const option = options.find((o) => o.requisitionID === event.target.value);
    /* istanbul ignore else */
    if (option) {
      handleSelection(option);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      const searchResults = getSearchResults(
        value,
        options,
        { primaryKey: 'jobTitle', secondaryKey: 'requisitionID' },
        null,
        null
      );
      setVisibleOptions(searchResults.matchedData);
    } else {
      setSearchString('');
      setVisibleOptions(options);
    }
    setSearchString(value);
  };

  const focusSearch = () => {
    (searchInputRef.current?.children[1] as HTMLInputElement)?.focus();
  };
  const handleItemKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      focusSearch();
    }
  };

  return (
    <KFDropdown
      id={id}
      data-testid={id}
      onOpen={() => {
        setSearchString('');
        setVisibleOptions(options);
        setTimeout(focusSearch, 50);
      }}
      sx={{
        width: theme?.typography.pxToRem(336),
        borderColor: colors.background.darkGray,
      }}
      MenuProps={{
        sx: {
          ul: {
            p: '0 !important',
            overflowY: 'auto',
            maxHeight: ({ typography }) => typography.pxToRem(440),
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: `${colors?.tertiary.white}`,
              width: ({ typography }) => typography.pxToRem(5),
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: '0.6rem',
              backgroundColor: `${colors?.background.darkGray}`,
            },
          },
        },
        PaperProps: {
          sx: {
            borderRadius: '0.5rem',
            boxShadow: '0.25rem 0.5rem 1rem rgba(48, 48, 108, 0.32)',
          },
        },
      }}
      defaultValue={defaultSelected?.requisitionID ?? allReqValue}
      value={selected}
      onChange={handleSelectionChange}
      inputProps={{
        htmlFor: 'req-select-dropdown',
        role: 'presentation none',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          right: '0',
          top: '0',
          backgroundColor: `${colors?.tertiary.white}`,
          zIndex: '1',
          px: '0.75rem',
          py: '1rem',
        }}
      >
        <KFSearchbar
          autoFocus
          id='req-select-search'
          data-testid='req-select-search'
          className='req-select-searchbox'
          placeholder={translate('findByJobTitleOrRequisitionId')}
          value={searchString}
          onChange={handleSearchChange}
          {...props}
          sx={{
            ...(props.InputProps?.sx as any),
            position: 'sticky',
            right: '0',
            top: '0',
            zIndex: '1',
            borderColor: `${colors.background.darkGray} !important`,
            color: `${colors.tertiary.darkGray} !important`,
            borderRadius: '0.25rem !important',
          }}
          InputProps={{
            ref: searchInputRef,
            sx: {
              width: '100% !important',
              borderColor: `${colors.background.darkGray} !important`,
              color: `${colors.tertiary.darkGray} !important`,
              borderRadius: '0.25rem !important',
            },
            onKeyDown: (e) => {
              /* istanbul ignore else */
              if (!(e.key === 'Tab' && e.shiftKey) && e.key !== 'Escape')
                e.stopPropagation();
            },
          }}
        />
      </Box>
      <MenuItem
        sx={{
          py: '0 !important',
          px: '0.75rem !important',
        }}
        value={allReqValue}
        id='allReqMenu'
        data-testid='allReqMenu'
        onKeyDown={handleItemKeyDown}
        onClick={() => {
          setSelected(allReqValue);
          handleSelection(null);
          setVisibleOptions(options);
        }}
      >
        {translate('allRequisitionsInHiring')}
      </MenuItem>
      {visibleOptions?.map((option, i) => (
        <MenuItem
          sx={{
            py: '0 !important',
            px: '0.75rem !important',
          }}
          id={`${id}-option-${i}`}
          data-testid={`${id}-option-${i}`}
          aria-label={option.jobTitle}
          key={`${option.jobTitle}-${option.requisitionID}-${i}`}
          value={option.requisitionID}
          onKeyDown={handleItemKeyDown}
        >
          <Tooltip title={`${option.jobTitle}${option.requisitionID}`}>
            <Typography
              overflow='hidden'
              textOverflow='ellipsis'
              width='290px'
              whiteSpace='nowrap'
            >
              {option.jobTitle} {option.requisitionID}
            </Typography>
          </Tooltip>
        </MenuItem>
      ))}
    </KFDropdown>
  );
};

export default withTranslation()(DropdownWithSearch);
